import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { 
  Search, 
  MapPin, 
  Star, 
  Clock, 
  Truck,
  Pill,
  ShoppingCart,
  Utensils,
  Zap,
  Car,
  Hospital,
  Filter,
  ChevronRight,
  CheckCircle2,
  Phone
} from 'lucide-react';

const CATEGORY_ICONS = {
  'Pharmacy': Pill,
  'Grocery': ShoppingCart,
  'Restaurant': Utensils,
  'Utility': Zap,
  'Transport': Car,
  'Hospital': Hospital
};

const CATEGORY_COLORS = {
  'Pharmacy': 'bg-green-100 text-green-700 border-green-200',
  'Grocery': 'bg-blue-100 text-blue-700 border-blue-200',
  'Restaurant': 'bg-orange-100 text-orange-700 border-orange-200',
  'Utility': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Transport': 'bg-burgundy-100 text-burgundy-700 border-burgundy-200',
  'Hospital': 'bg-red-100 text-red-700 border-red-200'
};

const MarketplacePage = () => {
  const { vendors, getVendorsByCategory } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedVendor, setSelectedVendor] = useState(null);

  const categories = ['All', 'Pharmacy', 'Grocery', 'Restaurant', 'Utility', 'Transport', 'Hospital'];

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          vendor.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || vendor.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const VendorCard = ({ vendor }) => {
    const CategoryIcon = CATEGORY_ICONS[vendor.category] || ShoppingCart;
    const categoryColor = CATEGORY_COLORS[vendor.category] || 'bg-gray-100 text-gray-700';

    return (
      <div
        onClick={() => setSelectedVendor(vendor)}
        className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
        data-testid={`vendor-card-${vendor.id}`}
      >
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className={`w-14 h-14 rounded-xl ${categoryColor.split(' ')[0]} flex items-center justify-center`}>
              <CategoryIcon className={`w-7 h-7 ${categoryColor.split(' ')[1]}`} />
            </div>
            {vendor.verified && (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                <CheckCircle2 className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>

          <h3 className="font-semibold text-lg text-gray-900 mb-1 group-hover:text-burgundy-600 transition-colors">
            {vendor.name}
          </h3>

          <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {vendor.city}, {vendor.state}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-sm">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="font-medium text-gray-900">{vendor.rating}</span>
              </span>
              {vendor.deliveryAvailable && (
                <span className="flex items-center gap-1 text-xs text-burgundy-600 bg-burgundy-50 px-2 py-1 rounded-full">
                  <Truck className="w-3 h-3" />
                  Delivers
                </span>
              )}
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${categoryColor}`}>
              {vendor.category}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const VendorDetail = ({ vendor, onClose }) => {
    const CategoryIcon = CATEGORY_ICONS[vendor.category] || ShoppingCart;
    const categoryColor = CATEGORY_COLORS[vendor.category] || 'bg-gray-100 text-gray-700';

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-xl ${categoryColor.split(' ')[0]} flex items-center justify-center`}>
                  <CategoryIcon className={`w-8 h-8 ${categoryColor.split(' ')[1]}`} />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-bold text-gray-900">{vendor.name}</h2>
                  <p className="text-gray-500 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {vendor.address || `${vendor.city}, ${vendor.state}`}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-bold text-lg text-gray-900">{vendor.rating}</span>
                </div>
                <p className="text-xs text-gray-500">Rating</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <p className="font-bold text-lg text-gray-900">{vendor.totalOrders}</p>
                <p className="text-xs text-gray-500">Orders</p>
              </div>
              {vendor.averageDeliveryTime > 0 && (
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="font-bold text-lg text-gray-900">{vendor.averageDeliveryTime}</span>
                  </div>
                  <p className="text-xs text-gray-500">min delivery</p>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Operating Hours</span>
                <span className="font-medium text-gray-900">{vendor.operatingHours}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Delivery</span>
                <span className={`font-medium ${vendor.deliveryAvailable ? 'text-green-600' : 'text-gray-400'}`}>
                  {vendor.deliveryAvailable ? `Within ${vendor.deliveryRadius}km` : 'Not available'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Verified Since</span>
                <span className="font-medium text-gray-900">
                  {new Date(vendor.verifiedSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>

            {/* Popular Items */}
            {vendor.popularItems && vendor.popularItems.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Popular Items</h3>
                <div className="flex flex-wrap gap-2">
                  {vendor.popularItems.map((item, index) => (
                    <span key={index} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button className="flex-1 px-4 py-3 bg-burgundy-600 text-white font-medium rounded-xl hover:bg-burgundy-700 transition-colors flex items-center justify-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Create Order
              </button>
              <button className="p-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors">
                <Phone className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="marketplace-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-gray-900">Vendor Marketplace</h1>
              <p className="text-gray-500 mt-1">Order supplies for your assigned dependants</p>
            </div>
            <span className="px-3 py-1.5 bg-burgundy-100 text-burgundy-700 text-sm font-medium rounded-full">
              Powered by Pebbles
            </span>
          </div>

          {/* Search & Filters */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search vendors by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
            {categories.map(category => {
              const CategoryIcon = category === 'All' ? Filter : CATEGORY_ICONS[category];
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-burgundy-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {CategoryIcon && <CategoryIcon className="w-4 h-4" />}
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-3xl font-serif font-bold text-gray-900">{vendors.length}</p>
            <p className="text-sm text-gray-500">Verified Vendors</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-3xl font-serif font-bold text-green-600">
              {vendors.filter(v => v.category === 'Pharmacy').length}
            </p>
            <p className="text-sm text-gray-500">Pharmacies</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-3xl font-serif font-bold text-blue-600">
              {vendors.filter(v => v.category === 'Grocery').length}
            </p>
            <p className="text-sm text-gray-500">Grocery Stores</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-3xl font-serif font-bold text-burgundy-600">
              {vendors.filter(v => v.deliveryAvailable).length}
            </p>
            <p className="text-sm text-gray-500">With Delivery</p>
          </div>
        </div>

        {/* Vendors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVendors.map(vendor => (
            <VendorCard key={vendor.id} vendor={vendor} />
          ))}
        </div>

        {filteredVendors.length === 0 && (
          <div className="text-center py-16">
            <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-xl text-gray-900">No vendors found</h3>
            <p className="text-gray-500 mt-1">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Vendor Detail Modal */}
      {selectedVendor && (
        <VendorDetail vendor={selectedVendor} onClose={() => setSelectedVendor(null)} />
      )}
    </div>
  );
};

export default MarketplacePage;
